/* src/pages/Profile.jsx — COMPLETE CODE with Subscription Management */
import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "../firebase";
import { doc, getDoc, updateDoc, collection, getDocs, query, orderBy } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, listAll } from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Toast from "./Toast"; // Assuming Toast is in the same directory
import "./Profile.css";
import { useAuth } from "../context/AuthContext";
import { calculateProfileCompletion, getCompletionMessage } from "../utils/profileHelpers";

const Profile = () => {
    const { currentUser, loading: authLoading, logout } = useAuth();
    const [userData, setUserData] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [photoURL, setPhotoURL] = useState("");
    const [editingField, setEditingField] = useState(null);
    const [tempValue, setTempValue] = useState("");
    const [toast, setToast] = useState(null);
    const [profileCompletion, setProfileCompletion] = useState({ percentage: 100, isComplete: true, missingFields: [] });
    const [expandedTestIndex, setExpandedTestIndex] = useState(null);
    const navigate = useNavigate();

    const [questionImages, setQuestionImages] = useState({});
    const [analysisLoading, setAnalysisLoading] = useState(false);
    
    const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
    const [activeSubject, setActiveSubject] = useState('Physics');

    const [isProfileExpanded, setIsProfileExpanded] = useState(true);

    // Combined Result popup
    const [analysisPopupTest, setAnalysisPopupTest] = useState(null);
    const [analysisPopupImages, setAnalysisPopupImages] = useState({});
    const [analysisPopupLoading, setAnalysisPopupLoading] = useState(false);
    const [analysisSelectedQ, setAnalysisSelectedQ] = useState(null);
    const [analysisActiveSubject, setAnalysisActiveSubject] = useState('Physics');
    const [analysisQLoading, setAnalysisQLoading] = useState(false);

    // Search and Pagination State
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const TESTS_PER_PAGE = 6;

    // Modal State (keep existing)
    const [modalImage, setModalImage] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const imageRef = useRef(null);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const startY = useRef(0);
    const startPanX = useRef(0);
    const startPanY = useRef(0);
    // Cache for Firebase Storage image maps: keyed by storagePath
    const imageCache = useRef({});
    // Tracks the in-flight Image() preload so it can be cancelled
    const preloadImgRef = useRef(null);

    useEffect(() => {
        if (authLoading || !currentUser) return;
        const fetchUserData = async () => {
            setProfileLoading(true);
            try {
                const userDoc = await getDoc(doc(db, "users", currentUser.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    
                    if (!data.testsTaken) data.testsTaken = [];
                    // Read from scalable subcollection; merge with legacy array (backward compat)
                    try {
                        const subRef = collection(db, "users", currentUser.uid, "testResults");
                        const subSnap = await getDocs(query(subRef, orderBy("date", "desc")));
                        const subResults = subSnap.docs.map(d => d.data());
                        const subKeys = new Set(subResults.map(r => `${r.testName}|${(r.date||'').substring(0,10)}`));
                        data.testsTaken.forEach(t => {
                            const key = `${t.testName}|${(t.date||'').substring(0,10)}`;
                            if (!subKeys.has(key)) subResults.push(t);
                        });
                        subResults.sort((a, b) => new Date(b.date) - new Date(a.date));
                        data.testsTaken = subResults;
                    } catch (subErr) {
                        console.warn("Subcollection read failed, using legacy array:", subErr);
                        data.testsTaken.sort((a, b) => new Date(b.date) - new Date(a.date));
                    }
                    setUserData(data);
                    setPhotoURL(data.photoURL || currentUser.photoURL || "");
                    const completion = calculateProfileCompletion(data);
                    setProfileCompletion(completion);
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
            }
            setProfileLoading(false);
        };
        fetchUserData();
    }, [currentUser, authLoading]);

    const showToast = (message, type = "success") => setToast({ message, type });

    // Filter and Pagination Logic
    const filteredTests = userData?.testsTaken?.filter(test => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            test.testName?.toLowerCase().includes(query) ||
            test.date?.toLowerCase().includes(query) ||
            new Date(test.date).toLocaleDateString().toLowerCase().includes(query)
        );
    }) || [];

    const totalPages = Math.ceil(filteredTests.length / TESTS_PER_PAGE);
    const startIndex = (currentPage - 1) * TESTS_PER_PAGE;
    const endIndex = startIndex + TESTS_PER_PAGE;
    const paginatedTests = filteredTests.slice(startIndex, endIndex);

    // Reset to page 1 when search query changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ... (rest of the original utility functions: toggleDetails, handleUpdate, etc.) ...
    const toggleDetails = async (index) => { /* ... existing logic ... */
        if (expandedTestIndex === index) {
            setExpandedTestIndex(null);
            return;
        }

        setActiveQuestionIndex(0);
        setActiveSubject('Physics');
        setAnalysisLoading(true);
        setExpandedTestIndex(index);
        setQuestionImages({});
        
        const test = userData.testsTaken[index];
        if (!test.storagePath || !test.questionDetails) {
            setAnalysisLoading(false);
            return;
        }

        try {
            const isObjectBased = typeof test.questionDetails === 'object' && !Array.isArray(test.questionDetails) && test.questionDetails !== null;
            
            if (isObjectBased) {
                setAnalysisLoading(false);
            } 
            else if (Array.isArray(test.questionDetails)) { 
                const fullStoragePath = test.storagePath;
                const folderRef = ref(storage, fullStoragePath);
                const res = await listAll(folderRef);
                const allImageUrls = await Promise.all(
                    res.items.map(async (itemRef) => {
                        const name = itemRef.name;
                        const match = name.match(/(\d+)/);
                        const id = match ? parseInt(match[1], 10) : null;
                        if (id === null) return null;
                        const url = await getDownloadURL(itemRef);
                        return { id, url };
                    })
                );
                
                const imageMap = allImageUrls
                    .filter(item => item !== null)
                    .reduce((acc, item) => {
                        acc[item.id] = item.url;
                        return acc;
                    }, {});
                
                setQuestionImages(imageMap);
                setAnalysisLoading(false);
            } else {
                setAnalysisLoading(false);
            }

        } catch (error) {
            console.error("Error loading question analysis:", error);
            showToast("Could not load question analysis.", "error");
            setAnalysisLoading(false);
        }
    };

    const handleQuestionNavigation = (direction, totalQuestions) => {
        setActiveQuestionIndex(prev => {
            const newIndex = prev + direction;
            return newIndex >= 0 && newIndex < totalQuestions ? newIndex : prev;
        });
    };

    const handleSubjectChange = (subject) => {
        setActiveSubject(subject);
        setActiveQuestionIndex(0);
    };

    // selectAnalysisQ: shows spinner while the question image actually downloads.
    // If there is an image URL, we preload it via Image() so the spinner stays until
    // the browser has the image in its HTTP cache. When done, React renders the panel
    // and <img src> loads from cache — no gap, no delay.
    // For questions with no image, spinner clears after 1 rAF (~16 ms).
    const selectAnalysisQ = (id, imgUrl) => {
        // Cancel any in-flight preload from a previous click
        if (preloadImgRef.current) {
            preloadImgRef.current.onload = null;
            preloadImgRef.current.onerror = null;
            preloadImgRef.current = null;
        }
        setAnalysisSelectedQ(id);
        if (id === null) { setAnalysisQLoading(false); return; }
        setAnalysisQLoading(true);
        if (!imgUrl) {
            // No image — content is already in memory; clear spinner next frame
            requestAnimationFrame(() => setAnalysisQLoading(false));
            return;
        }
        // Preload the image; spinner clears only when download completes
        const img = new Image();
        const done = () => { preloadImgRef.current = null; setAnalysisQLoading(false); };
        img.onload  = done;
        img.onerror = done;   // show content even if image fails to load
        img.src = imgUrl;
        preloadImgRef.current = img;
    };

    const selectAnalysisSubject = (s, firstQImgUrl) => {
        setAnalysisActiveSubject(s);
        selectAnalysisQ(0, firstQImgUrl);
    };

    // Cancel preload and clear spinner when the popup is closed
    useEffect(() => {
        if (!analysisPopupTest) {
            if (preloadImgRef.current) {
                preloadImgRef.current.onload = null;
                preloadImgRef.current.onerror = null;
                preloadImgRef.current = null;
            }
            setAnalysisQLoading(false);
        }
    }, [analysisPopupTest]);

    const openAnalysisPopup = async (t) => {
        const isObjBased = typeof t.questionDetails === 'object' && !Array.isArray(t.questionDetails) && t.questionDetails !== null;
        setAnalysisPopupTest(t);
        // Default: select first question so the detail panel opens automatically
        if (isObjBased && t.questionDetails) {
            const firstSubj = Object.keys(t.questionDetails)[0] || 'Physics';
            setAnalysisActiveSubject(firstSubj);
            setAnalysisSelectedQ(0); // first question index for NEET/ADV
        } else if (Array.isArray(t.questionDetails) && t.questionDetails.length > 0) {
            setAnalysisActiveSubject('Physics');
            setAnalysisSelectedQ(t.questionDetails[0].questionId); // first questionId for JEE
        } else {
            setAnalysisActiveSubject('Physics');
            setAnalysisSelectedQ(null);
        }
        setAnalysisPopupImages({});
        if (!isObjBased && Array.isArray(t.questionDetails) && t.storagePath) {
            const cacheKey = t.storagePath;
            if (imageCache.current[cacheKey]) {
                // Use cached images instantly — no loading needed
                setAnalysisPopupImages(imageCache.current[cacheKey]);
            } else {
                setAnalysisPopupLoading(true);
                try {
                    const folderRef = ref(storage, t.storagePath);
                    const res = await listAll(folderRef);
                    const imgs = await Promise.all(res.items.map(async item => {
                        const m = item.name.match(/(\d+)/);
                        const id = m ? parseInt(m[1], 10) : null;
                        if (id === null) return null;
                        return { id, url: await getDownloadURL(item) };
                    }));
                    const imgMap = imgs.filter(Boolean).reduce((acc, x) => { acc[x.id] = x.url; return acc; }, {});
                    imageCache.current[cacheKey] = imgMap;
                    setAnalysisPopupImages(imgMap);
                } catch (e) { console.error(e); }
                setAnalysisPopupLoading(false);
            }
        }
    };

    const handleUpdate = async (field, value) => {
        if (!currentUser) return;
        showToast("Updating profile...", "success");
        try {
            await updateDoc(doc(db, "users", currentUser.uid), { [field]: value });
            const updatedData = { ...userData, [field]: value };
            setUserData(updatedData);
            
            // Recalculate profile completion
            const completion = calculateProfileCompletion(updatedData);
            setProfileCompletion(completion);
            
            setEditingField(null);
            showToast(`${field} updated successfully!`);
        } catch (error) {
            showToast("Failed to update. Try again.", "error");
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !currentUser) return;
        showToast("Uploading photo...", "success");
        try {
            const storageRef = ref(storage, `profilePhotos/${currentUser.uid}`);
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);
            await updateDoc(doc(db, "users", currentUser.uid), { photoURL: downloadURL });
            await updateProfile(currentUser, { photoURL: downloadURL });
            setPhotoURL(downloadURL);
            showToast("Profile photo updated!");
        } catch (error) {
            showToast("Failed to upload photo.", "error");
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate("/");
        } catch (error) {
            showToast("Logout failed. Please try again.", "error");
        }
    };

    const openImageModal = (imageUrl) => {
        if (!imageUrl) {
            showToast("Image not available for this question.", "error");
            return;
        }
        setModalImage(imageUrl);
        document.body.style.overflow = 'hidden';
    };

    const closeImageModal = () => {
        setModalImage(null);
        document.body.style.overflow = '';
    };

    const handleZoom = (direction) => setZoomLevel(prev => Math.max(1, prev + direction * 0.1));
    const handleMouseDown = (e) => { if (zoomLevel > 1) { isDragging.current = true; startX.current = e.clientX; startY.current = e.clientY; startPanX.current = panX; startPanY.current = panY; e.preventDefault(); } };
    const handleMouseMove = (e) => { if (!isDragging.current) return; const dx = e.clientX - startX.current; const dy = e.clientY - startY.current; setPanX(startPanX.current + dx); setPanY(startPanY.current + dy); };
    const handleMouseUp = () => { isDragging.current = false; };
    const handleContextMenu = (e) => e.preventDefault();
    
    const renderAnswer = (answer) => {
        if (answer === null || answer === undefined) return "Not Answered";
        if (Array.isArray(answer)) {
            if (answer.length === 0) return "Not Answered";
            return `[${[...answer].sort().join(", ")}]`;
        }
        return String(answer);
    };

    if (authLoading || profileLoading) return <p className="loading-text">Loading...</p>;
    if (!userData) return <p className="loading-text">Unable to load profile.</p>;

    return (
        <div className="profile-page-container">
            <aside className="profile-sidebar">
                {/* Premium Profile Header */}
                <div className="profile-identity">
                    <div className="profile-photo-wrapper">
                        <div className="photo-ring"></div>
                        <img src={photoURL || "/default-avatar.png"} alt="profile" className="profile-photo" />
                        <label className="photo-upload-label">
                            <input type="file" onChange={handlePhotoUpload} style={{ display: "none" }} />
                            <span>📷</span>
                        </label>
                    </div>
                    <h2 className="profile-username">{userData.username}</h2>
                    <p className="profile-email">{userData.email}</p>
                    <div className="profile-action-buttons">
                        <button
                            className="btn-my-tests"
                            onClick={() => navigate('/purchased-tests')}
                        >
                            <span className="btn-icon">📚</span>
                            <span>My Tests</span>
                        </button>
                        <button className="btn-logout" onClick={handleLogout}>
                            <span className="btn-icon">🚪</span>
                            <span>Logout</span>
                        </button>
                    </div>
                </div>

                <div className="sidebar-divider"></div>

                {/* Profile Completion Banner */}
                {!profileCompletion.isComplete && (
                    <div className="completion-banner">
                        <div className="completion-header">
                            <div className="completion-info">
                                <h3 className="completion-title">📋 Complete Your Profile</h3>
                                <p className="completion-message">{getCompletionMessage(profileCompletion.percentage)}</p>
                            </div>
                            <div className="completion-percentage">
                                <div className="circular-progress">
                                    <svg className="progress-ring" width="52" height="52">
                                        <circle
                                            className="progress-ring-circle"
                                            stroke="#e5e7eb"
                                            strokeWidth="4"
                                            fill="transparent"
                                            r="22"
                                            cx="26"
                                            cy="26"
                                        />
                                        <circle
                                            className="progress-ring-circle-fill"
                                            stroke={profileCompletion.percentage >= 75 ? '#10b981' : profileCompletion.percentage >= 50 ? '#f59e0b' : '#ef4444'}
                                            strokeWidth="4"
                                            fill="transparent"
                                            r="22"
                                            cx="26"
                                            cy="26"
                                            style={{
                                                strokeDasharray: `${2 * Math.PI * 22}`,
                                                strokeDashoffset: `${2 * Math.PI * 22 * (1 - profileCompletion.percentage / 100)}`,
                                                transition: 'stroke-dashoffset 0.5s ease'
                                            }}
                                        />
                                    </svg>
                                    <span className="percentage-text">{profileCompletion.percentage}%</span>
                                </div>
                            </div>
                        </div>
                        {profileCompletion.missingFields.length > 0 && (
                            <div className="missing-fields-list" style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                <span className="missing-fields-title" style={{ margin: 0, padding: 0 }}
                                >Missing fields: <ul className="missing-fields-items" style={{ display: 'inline-flex', gap: '4px', margin: 0, padding: 0, listStyle: 'none' }}>
                                    {profileCompletion.missingFields.map(field => (
                                        <li key={field.key} style={{ display: 'inline', margin: 0, padding: 0 }}>❌ {field.label}</li>
                                    ))}
                                </ul>
                                </span>
                            </div>
                        )}
                    </div>
                )}

                <div className="sidebar-divider"></div>

                {/* Account Details Section */}
                <div className="account-details-section">
                    <h3 className="section-heading">📋 Account Details</h3>
                    <div className="profile-info">
                            <div className="profile-row">
                                <span className="profile-label">📱 Mobile</span>
                                {editingField === 'mobile' ? (
                                    <div className="edit-mode">
                                        <input value={tempValue} onChange={(e) => setTempValue(e.target.value)} placeholder="Enter mobile number" />
                                        <div className="edit-actions">
                                            <button onClick={() => handleUpdate('mobile', tempValue)} className="btn-save">✔ Save</button>
                                            <button onClick={() => setEditingField(null)} className="btn-cancel">✖ Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="view-mode">
                                        <span className="field-value">{userData.mobile || "Not provided"}</span>
                                        <button onClick={() => { setEditingField('mobile'); setTempValue(userData.mobile || ''); }} className="btn-edit">✎ Edit</button>
                                    </div>
                                )}
                            </div>
                            <div className="profile-row">
                                <span className="profile-label">🏫 Class</span>
                                {editingField === 'class' ? (
                                    <div className="edit-mode">
                                        <select value={tempValue} onChange={(e) => setTempValue(e.target.value)}>
                                            <option value="">Select Class</option><option value="6">Class 6</option><option value="7">Class 7</option><option value="8">Class 8</option><option value="9">Class 9</option><option value="10">Class 10</option><option value="11">Class 11</option><option value="12">Class 12</option><option value="Dropper">Dropper</option>
                                        </select>
                                        <div className="edit-actions">
                                            <button onClick={() => handleUpdate('class', tempValue)} className="btn-save">✔ Save</button>
                                            <button onClick={() => setEditingField(null)} className="btn-cancel">✖ Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="view-mode">
                                        <span className="field-value">{userData.class || "Not selected"}</span>
                                        <button onClick={() => { setEditingField('class'); setTempValue(userData.class || ''); }} className="btn-edit">✎ Edit</button>
                                    </div>
                                )}
                            </div>
                            <div className="profile-row">
                                <span className="profile-label">🎯 Preparing For</span>
                                {editingField === 'preparingFor' ? (
                                    <div className="edit-mode">
                                        <select value={tempValue} onChange={(e) => setTempValue(e.target.value)}>
                                            <option value="">Select Exam</option><option value="School">School Exams</option><option value="Boards">Board Exams</option><option value="JEE">JEE (Main/Advanced)</option><option value="NEET">NEET</option>
                                        </select>
                                        <div className="edit-actions">
                                            <button onClick={() => handleUpdate('preparingFor', tempValue)} className="btn-save">✔ Save</button>
                                            <button onClick={() => setEditingField(null)} className="btn-cancel">✖ Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="view-mode">
                                        <span className="field-value">{userData.preparingFor || "Not selected"}</span>
                                        <button onClick={() => { setEditingField('preparingFor'); setTempValue(userData.preparingFor || ''); }} className="btn-edit">✎ Edit</button>
                                    </div>
                                )}
                            </div>
                            <div className="profile-row">
                                <span className="profile-label">🏠 Address</span>
                                {editingField === 'address' ? (
                                    <div className="edit-mode">
                                        <input value={tempValue} onChange={(e) => setTempValue(e.target.value)} placeholder="Enter your address" />
                                        <div className="edit-actions">
                                            <button onClick={() => handleUpdate('address', tempValue)} className="btn-save">✔ Save</button>
                                            <button onClick={() => setEditingField(null)} className="btn-cancel">✖ Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="view-mode">
                                        <span className="field-value">{userData.address || "Not provided"}</span>
                                        <button onClick={() => { setEditingField('address'); setTempValue(userData.address || ''); }} className="btn-edit">✎ Edit</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
            </aside>

            <main className="test-history-main">
                <div className="main-header-wrapper">
                    <div className="header-left">
                        <h1 className="main-header">
                            <span className="header-icon">📊</span>
                            Test Attempt History ({userData?.testsTaken?.length || 0})
                        </h1>
                    </div>
                    {userData?.testsTaken && userData.testsTaken.length > 0 && (
                        <div className="header-right">
                            <div className="compact-search-box">
                                <span className="search-icon">🔍</span>
                                <input
                                    type="text"
                                    placeholder="Filter tests..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="compact-search-input"
                                />
                                {searchQuery && (
                                    <button 
                                        className="clear-search-btn" 
                                        onClick={() => setSearchQuery("")}
                                        title="Clear search"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="test-history-content">
                    {!userData.testsTaken || userData.testsTaken.length === 0 ? (
                        <p>No tests taken yet.</p>
                    ) : filteredTests.length === 0 ? (
                        <div className="no-results">
                            <p>No tests match your search.</p>
                            <button className="clear-search-btn-large" onClick={() => setSearchQuery("")}>
                                Clear Search
                            </button>
                        </div>
                    ) : (
                        <>
                        <ul>
                            {paginatedTests.map((t, idx) => {
                                // Get the original index in the full testsTaken array
                                const originalIndex = userData.testsTaken.findIndex(test => test === t);
                                const i = originalIndex;
                                
                                const isObjectBased = typeof t.questionDetails === 'object' && !Array.isArray(t.questionDetails) && t.questionDetails !== null;
                                const isNeet = isObjectBased && t.testName?.toLowerCase().includes('neet');
                                const isAdvanced = isObjectBased && !isNeet;

                                const neetMaxMarks = isNeet ? {
                                    Physics: (t.questionDetails?.Physics?.length || 0) * 4,
                                    Chemistry: (t.questionDetails?.Chemistry?.length || 0) * 4,
                                    Biology: (t.questionDetails?.Biology?.length || 0) * 4,
                                } : {};
                                
                                const advMaxSubjectMarks = 60;

                                return (
                                    <li key={i} className="test-item">
                                        <p className="test-item-title">
                                            <strong>{t.testName}</strong>
                                            {t.attempt && <span className="attempt-badge"> (Attempt {t.attempt})</span>}
                                        </p>
                                        
                                        <div className="test-item-details">
                                            <span className="test-detail-item"><strong>Score:</strong> {t.score} / {t.total}</span>
                                            <span className="test-detail-item"><strong>Correct:</strong> <span className="stat-correct">{t.correct}</span></span>
                                            <span className="test-detail-item"><strong>Incorrect:</strong> <span className="stat-incorrect">{t.incorrect}</span></span>
                                        </div>
                                        <div className="test-item-details">
                                            <span className="test-detail-item"><strong>Attempted:</strong> {t.attempted} / {t.totalQuestions}</span>
                                            {t.date && (<span className="test-detail-item"><strong>Date:</strong> {new Date(t.date).toLocaleDateString()}</span>)}
                                        </div>
                                        
                                        {isObjectBased && t.subjectScores && (
                                            <div className="subject-scores-grid">
                                                <span className="subject-score phy">
                                                    <strong>Physics:</strong> {typeof t.subjectScores?.Physics === 'object' ? (t.subjectScores.Physics?.score ?? 'N/A') : (t.subjectScores?.Physics ?? 'N/A')} / {isNeet ? neetMaxMarks.Physics : advMaxSubjectMarks}
                                                </span>
                                                <span className="subject-score chem">
                                                    <strong>Chemistry:</strong> {typeof t.subjectScores?.Chemistry === 'object' ? (t.subjectScores.Chemistry?.score ?? 'N/A') : (t.subjectScores?.Chemistry ?? 'N/A')} / {isNeet ? neetMaxMarks.Chemistry : advMaxSubjectMarks}
                                                </span>
                                                {isNeet ? (
                                                    <span className="subject-score bio">
                                                        <strong>Biology:</strong> {typeof t.subjectScores?.Biology === 'object' ? (t.subjectScores.Biology?.score ?? 'N/A') : (t.subjectScores?.Biology ?? 'N/A')} / {neetMaxMarks.Biology}
                                                    </span>
                                                ) : (
                                                    <span className="subject-score math">
                                                        <strong>Mathematics:</strong> {typeof t.subjectScores?.Mathematics === 'object' ? (t.subjectScores.Mathematics?.score ?? 'N/A') : (t.subjectScores?.Mathematics ?? 'N/A')} / {advMaxSubjectMarks}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {t.questionDetails && (
                                            <button className="popup-view-result-btn" onClick={() => openAnalysisPopup(t)}>📊 Detailed Analysis</button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <button
                                    className="pagination-btn"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    ← Previous
                                </button>
                                
                                <div className="pagination-numbers">
                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNumber = index + 1;
                                        // Show first page, last page, current page, and pages around current
                                        if (
                                            pageNumber === 1 ||
                                            pageNumber === totalPages ||
                                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={pageNumber}
                                                    className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                                                    onClick={() => handlePageChange(pageNumber)}
                                                >
                                                    {pageNumber}
                                                </button>
                                            );
                                        } else if (
                                            pageNumber === currentPage - 2 ||
                                            pageNumber === currentPage + 2
                                        ) {
                                            return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                <button
                                    className="pagination-btn"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next →
                                </button>
                            </div>
                        )}

                        <div className="pagination-info">
                            Showing {startIndex + 1}-{Math.min(endIndex, filteredTests.length)} of {filteredTests.length} test{filteredTests.length !== 1 ? 's' : ''}
                        </div>
                        </>
                    )}
                </div>
            </main>

            {toast && (
                <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
            )}

            {/* ══ COMBINED RESULT POPUP (Summary + Analysis) ══ */}
            {analysisPopupTest && (
                <div className="prof-popup-overlay" onClick={() => { setAnalysisPopupTest(null); setAnalysisSelectedQ(null); }}>
                    <div className="prof-popup-content prof-popup-wide" onClick={e => e.stopPropagation()}>
                        <div className="prof-popup-header">
                            <h2 className="prof-popup-title">📊 Test Result &amp; Analysis</h2>
                            <button className="prof-popup-close" onClick={() => { setAnalysisPopupTest(null); setAnalysisSelectedQ(null); }}>✕</button>
                        </div>
                        <div className="prof-popup-scroll">
                            {(() => {
                                const t = analysisPopupTest;
                                const accuracy = (t.correct > 0 || t.incorrect > 0)
                                    ? ((t.correct / (t.correct + t.incorrect)) * 100).toFixed(1)
                                    : '0.0';
                                const unattempted = (t.totalQuestions || 0) - (t.attempted || 0);
                                const isObjBased = typeof t.questionDetails === 'object' && !Array.isArray(t.questionDetails) && t.questionDetails !== null;

                                // Compute per-subject stats from questionDetails (handles all data formats + legacy)
                                const getDisplaySubjectScores = () => {
                                    if (isObjBased && t.questionDetails) {
                                        const result = {};
                                        const numSubjects = Object.keys(t.questionDetails).length || 1;
                                        Object.entries(t.questionDetails).forEach(([subj, questions]) => {
                                            const correct = questions.filter(q => q.status === 'correct').length;
                                            const incorrect = questions.filter(q => q.status === 'incorrect').length;
                                            const unattemptedS = questions.filter(q => q.status === 'unattempted').length;
                                            const score = questions.reduce((sum, q) => sum + (q.marks || 0), 0);
                                            const attempted = correct + incorrect;
                                            const totalPerSubj = t.total ? Math.round(t.total / numSubjects) : questions.length * 4;
                                            result[subj] = { score, correct, incorrect, unattempted: unattemptedS, accuracy: attempted > 0 ? ((correct / attempted) * 100).toFixed(1) : '0.0', total: totalPerSubj };
                                        });
                                        return result;
                                    }
                                    if (Array.isArray(t.questionDetails) && t.questionDetails.length > 0) {
                                        const total = t.questionDetails.length;
                                        const subjInfer = (qId) => {
                                            if (total === 75) { if (qId <= 25) return 'Physics'; if (qId <= 50) return 'Chemistry'; return 'Mathematics'; }
                                            if (total === 90) { if (qId <= 30) return 'Physics'; if (qId <= 60) return 'Chemistry'; return 'Mathematics'; }
                                            const th = Math.ceil(total / 3); if (qId <= th) return 'Physics'; if (qId <= th * 2) return 'Chemistry'; return 'Mathematics';
                                        };
                                        const result = { Physics: { score: 0, correct: 0, incorrect: 0, unattempted: 0, total: 0 }, Chemistry: { score: 0, correct: 0, incorrect: 0, unattempted: 0, total: 0 }, Mathematics: { score: 0, correct: 0, incorrect: 0, unattempted: 0, total: 0 } };
                                        t.questionDetails.forEach(q => {
                                            const subj = q.subject || subjInfer(q.questionId);
                                            if (!result[subj]) return;
                                            result[subj].score += (q.marks || 0);
                                            result[subj].total += 4;
                                            if (q.status === 'correct') result[subj].correct++;
                                            else if (q.status === 'incorrect') result[subj].incorrect++;
                                            else result[subj].unattempted++;
                                        });
                                        Object.values(result).forEach(s => { const att = s.correct + s.incorrect; s.accuracy = att > 0 ? ((s.correct / att) * 100).toFixed(1) : '0.0'; });
                                        return result;
                                    }
                                    // Fallback: use stored subjectScores only if values are objects
                                    if (t.subjectScores) {
                                        const firstVal = Object.values(t.subjectScores)[0];
                                        if (typeof firstVal === 'object' && firstVal !== null) return t.subjectScores;
                                    }
                                    return null;
                                };
                                const displaySubjectScores = getDisplaySubjectScores();

                                // Compute actual positive/negative mark contributions from stored per-question marks
                                const allPopupQFlat = isObjBased && t.questionDetails
                                    ? Object.values(t.questionDetails).flat()
                                    : Array.isArray(t.questionDetails) ? t.questionDetails : [];
                                const popupPosMarks = allPopupQFlat.length > 0
                                    ? allPopupQFlat.reduce((s, q) => (q.marks > 0 ? s + q.marks : s), 0)
                                    : (t.testType === 'adv' ? t.correct * 3 : t.score + (t.incorrect || 0));
                                const popupNegMarks = allPopupQFlat.length > 0
                                    ? allPopupQFlat.reduce((s, q) => (q.marks < 0 ? s + Math.abs(q.marks) : s), 0)
                                    : (t.incorrect || 0);

                                return (
                                    <>
                                        {/* ── SECTION 1: RESULT SUMMARY ── */}
                                        <section className="result-section">
                                            <h3 className="result-section-title">📋 Result Summary</h3>
                                            <p className="result-test-name">{t.testName}</p>
                                            <p className="result-candidate">� {userData?.name || currentUser?.displayName || ''}</p>
                                            <div className="result-top-cards">
                                                <div className="rtop-card score-card">
                                                    <div className="rtop-label">SCORE</div>
                                                    <div className="rtop-value">{t.score}<span className="rtop-max">/{t.total}</span></div>
                                                </div>
                                                <div className="rtop-card accuracy-card">
                                                    <div className="rtop-label">ACCURACY</div>
                                                    <div className="rtop-value">{accuracy}<span className="rtop-max">%</span></div>
                                                </div>
                                                <div className="rtop-card attempted-card">
                                                    <div className="rtop-label">ATTEMPTED</div>
                                                    <div className="rtop-value">{t.attempted}<span className="rtop-max">/{t.totalQuestions}</span></div>
                                                </div>
                                            </div>
                                            <div className="your-progress-box">
                                                <h4 className="progress-heading">Your Progress</h4>
                                                <div className="progress-row">
                                                    <div className="prog-item correct-prog">
                                                        <span className="prog-icon">✅</span>
                                                        <span className="prog-label">Correct</span>
                                                        <span className="prog-count">{t.correct}/{t.totalQuestions}</span>
                                                        <span className="prog-marks">+{popupPosMarks}</span>
                                                    </div>
                                                    <div className="prog-item incorrect-prog">
                                                        <span className="prog-icon">❌</span>
                                                        <span className="prog-label">Incorrect</span>
                                                        <span className="prog-count">{t.incorrect}/{t.totalQuestions}</span>
                                                        <span className="prog-marks">-{popupNegMarks}</span>
                                                    </div>
                                                    <div className="prog-item skipped-prog">
                                                        <span className="prog-icon">⏭</span>
                                                        <span className="prog-label">Skipped</span>
                                                        <span className="prog-count">{unattempted}/{t.totalQuestions}</span>
                                                        <span className="prog-marks">0</span>
                                                    </div>
                                                </div>
                                            </div>
                                            {displaySubjectScores && (
                                                <div className="subject-perf-table-wrapper">
                                                    <h4 className="subject-perf-heading">Section-wise Performance</h4>
                                                    <div className="subject-perf-table">
                                                        <div className="spt-header">
                                                            <span>Section</span><span>Score</span><span>Correct</span><span>Incorrect</span><span>Skipped</span><span>Accuracy</span>
                                                        </div>
                                                        {Object.entries(displaySubjectScores).map(([subj, s]) => (
                                                            <div key={subj} className="spt-row">
                                                                <span className={`spt-subject spt-${subj.toLowerCase().substring(0,4)}`}>{subj}</span>
                                                                <span>{s.score ?? 0}/{s.total ?? 0}</span>
                                                                <span className="spt-correct">{s.correct ?? 0}</span>
                                                                <span className="spt-incorrect">{s.incorrect ?? 0}</span>
                                                                <span className="spt-skipped">{s.unattempted ?? 0}</span>
                                                                <span>{s.accuracy ?? '0.0'}%</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </section>

                                        {/* ── SECTION 2: QUESTION-WISE ANALYSIS ── */}
                                        {t.questionDetails && (
                                            <section className="result-section">
                                                <h3 className="result-section-title">🔍 Question-wise Analysis</h3>
                                                {analysisPopupLoading ? (
                                                    <p className="loading-text">Loading question images...</p>
                                                ) : isObjBased ? (() => {
                                                    const subjects = Object.keys(t.questionDetails);
                                                    const questionsForSubject = t.questionDetails[analysisActiveSubject] || [];
                                                    const isAdv = t.testType === 'adv';
                                                    const ADV_STRUCTURE = [
                                                        { section: 1, count: 4, type: 'singleCorrect' },
                                                        { section: 2, count: 3, type: 'multipleCorrect' },
                                                        { section: 3, count: 6, type: 'integer' },
                                                        { section: 4, count: 4, type: 'matching' },
                                                    ];
                                                    const SEC_LABELS = { singleCorrect: 'Single Correct', multipleCorrect: 'Multiple Correct', integer: 'Integer Type', matching: 'Matching' };
                                                    const selQ = analysisSelectedQ !== null ? questionsForSubject[analysisSelectedQ] : null;
                                                    return (
                                                        <>
                                                            <p className="floating-q-hint">Tap a number to view details · 🟢 Correct &nbsp; 🔴 Incorrect &nbsp; ⚪ Skipped</p>
                                                            <div className="result-subject-tabs">
                                                                {subjects.map(s => (
                                                                    <button key={s} className={`result-subj-tab ${analysisActiveSubject===s ? 'active' : ''}`} onClick={() => selectAnalysisSubject(s, t.questionDetails[s]?.[0]?.img)}>{s}</button>
                                                                ))}
                                                            </div>
                                                            {isAdv ? (
                                                                <>
                                                                    {ADV_STRUCTURE.map(sec => {
                                                                        const secStart = ADV_STRUCTURE.slice(0, ADV_STRUCTURE.indexOf(sec)).reduce((s, x) => s + x.count, 0);
                                                                        const secDetails = questionsForSubject.slice(secStart, secStart + sec.count);
                                                                        return (
                                                                            <div key={sec.section} className="floating-subject-group">
                                                                                <div className="floating-subj-label">Section {sec.section}: {SEC_LABELS[sec.type] || sec.type}</div>
                                                                                <div className="floating-circles-row" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
                                                                                    {secDetails.map((q, localIdx) => {
                                                                                        const globalIdx = secStart + localIdx;
                                                                                        const sc = q.status==='correct' ? 'fcircle-correct' : (q.status==='incorrect'||q.status==='partial') ? 'fcircle-wrong' : 'fcircle-skip';
                                                                                        return (<button key={globalIdx} className={`floating-circle ${sc} ${analysisSelectedQ===globalIdx ? 'fcircle-selected' : ''}`} onClick={() => selectAnalysisQ(analysisSelectedQ===globalIdx ? null : globalIdx, q.img)} title={`Q${q.questionId}: ${q.status}`}>{q.questionId}</button>);
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                    {selQ && (
                                                                        <div key={analysisSelectedQ} className={`result-q-detail-panel ${selQ.status==='correct' ? 'panel-correct' : selQ.status==='incorrect' || selQ.status==='partial' ? 'panel-wrong' : 'panel-skip'}`}>
                                                                            <div className="rqdp-header">
                                                                                <span className="rqdp-qnum">Q{selQ.questionId} — {analysisActiveSubject}</span>
                                                                                <button className="rqdp-close" onClick={() => setAnalysisSelectedQ(null)}>✕</button>
                                                                            </div>
                                                                            {analysisQLoading ? (
                                                                                <div className="rqdp-spinner-wrap"><span className="rqdp-spinner"></span></div>
                                                                            ) : (<>
                                                                            {selQ.img && (
                                                                                <div className="rqdp-img-wrapper" onClick={() => openImageModal(selQ.img)} style={{cursor:'zoom-in'}}>
                                                                                    <img src={selQ.img} alt={`Q${selQ.questionId}`} className="rqdp-img" />
                                                                                    <span className="rqdp-zoom-hint">🔍 Tap to zoom</span>
                                                                                </div>
                                                                            )}
                                                                            <div className="rqdp-answers">
                                                                                <div className="rqdp-ans-row"><span className="rqdp-ans-label">Your Answer:</span><span className={`rqdp-ans-val ${selQ.status==='correct'?'ans-correct':selQ.status==='incorrect'||selQ.status==='partial'?'ans-wrong':'ans-skip'}`}>{JSON.stringify(selQ.userAnswer) || 'Not Answered'}</span></div>
                                                                                <div className="rqdp-ans-row"><span className="rqdp-ans-label">Correct Answer:</span><span className="rqdp-ans-val ans-correct">{JSON.stringify(selQ.correctAnswer) || '—'}</span></div>
                                                                                <div className="rqdp-ans-row"><span className="rqdp-ans-label">Marks:</span><span className="rqdp-ans-val">{selQ.marks > 0 ? `+${selQ.marks}` : selQ.marks}</span></div>
                                                                            </div>
                                                                            </>)}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className="floating-circles-row" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
                                                                        {questionsForSubject.map((q, idx) => {
                                                                            const sc = q.status==='correct' ? 'fcircle-correct' : q.status==='incorrect' ? 'fcircle-wrong' : 'fcircle-skip';
                                                                            return (<button key={idx} className={`floating-circle ${sc} ${analysisSelectedQ===idx ? 'fcircle-selected' : ''}`} onClick={() => selectAnalysisQ(analysisSelectedQ===idx ? null : idx, q.img)} title={`Q${q.questionId}: ${q.status}`}>{q.questionId}</button>);
                                                                        })}
                                                                    </div>
                                                                    {selQ && (
                                                                        <div key={analysisSelectedQ} className={`result-q-detail-panel ${selQ.status==='correct' ? 'panel-correct' : selQ.status==='incorrect' ? 'panel-wrong' : 'panel-skip'}`}>
                                                                            <div className="rqdp-header">
                                                                                <span className="rqdp-qnum">Q{selQ.questionId} — {analysisActiveSubject}</span>
                                                                                <button className="rqdp-close" onClick={() => setAnalysisSelectedQ(null)}>✕</button>
                                                                            </div>
                                                                            {analysisQLoading ? (
                                                                                <div className="rqdp-spinner-wrap"><span className="rqdp-spinner"></span></div>
                                                                            ) : (<>
                                                                            {selQ.img && (
                                                                                <div className="rqdp-img-wrapper" onClick={() => openImageModal(selQ.img)} style={{cursor:'zoom-in'}}>
                                                                                    <img src={selQ.img} alt={`Q${selQ.questionId}`} className="rqdp-img" />
                                                                                    <span className="rqdp-zoom-hint">🔍 Tap to zoom</span>
                                                                                </div>
                                                                            )}
                                                                            <div className="rqdp-answers">
                                                                                <div className="rqdp-ans-row"><span className="rqdp-ans-label">Your Answer:</span><span className={`rqdp-ans-val ${selQ.status==='correct'?'ans-correct':selQ.status==='incorrect'?'ans-wrong':'ans-skip'}`}>{selQ.userAnswer || 'Not Answered'}</span></div>
                                                                                <div className="rqdp-ans-row"><span className="rqdp-ans-label">Correct Answer:</span><span className="rqdp-ans-val ans-correct">{selQ.correctAnswer || '—'}</span></div>
                                                                                <div className="rqdp-ans-row"><span className="rqdp-ans-label">Marks:</span><span className="rqdp-ans-val">{selQ.marks > 0 ? `+${selQ.marks}` : selQ.marks}</span></div>
                                                                            </div>
                                                                            </>)}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    );
                                                })() : Array.isArray(t.questionDetails) ? (() => {
                                                    const total = t.questionDetails.length;
                                                    const inferSubj = (qId) => {
                                                        if (total===75) { if(qId<=25) return 'Physics'; if(qId<=50) return 'Chemistry'; return 'Mathematics'; }
                                                        if (total===90) { if(qId<=30) return 'Physics'; if(qId<=60) return 'Chemistry'; return 'Mathematics'; }
                                                        const th = Math.ceil(total/3);
                                                        if(qId<=th) return 'Physics'; if(qId<=th*2) return 'Chemistry'; return 'Mathematics';
                                                    };
                                                    const grouped = { Physics: [], Chemistry: [], Mathematics: [] };
                                                    t.questionDetails.forEach(q => { grouped[inferSubj(q.questionId)].push(q); });
                                                    const activeGrp = grouped[analysisActiveSubject] || [];
                                                    const selQ = analysisSelectedQ !== null ? t.questionDetails.find(q => q.questionId === analysisSelectedQ) : null;
                                                    const selSubj = selQ ? inferSubj(selQ.questionId) : null;
                                                    return (
                                                        <>
                                                            <p className="floating-q-hint">Tap a number to view question details · 🟢 Correct &nbsp; 🔴 Incorrect &nbsp; ⚪ Skipped</p>
                                                            <div className="floating-circles-row" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
                                                                {t.questionDetails.map(q => {
                                                                    const sc = q.status==='correct' ? 'fcircle-correct' : q.status==='incorrect' ? 'fcircle-wrong' : 'fcircle-skip';
                                                                    return (<button key={q.questionId} className={`floating-circle ${sc} ${analysisSelectedQ===q.questionId ? 'fcircle-selected' : ''}`} onClick={() => selectAnalysisQ(analysisSelectedQ===q.questionId ? null : q.questionId, analysisPopupImages[q.questionId])} title={`Q${q.questionId}: ${q.status}`}>{q.questionId}</button>);
                                                                })}
                                                            </div>
                                                            {selQ && (
                                                                <div key={analysisSelectedQ} className={`result-q-detail-panel ${selQ.status==='correct'?'panel-correct':selQ.status==='incorrect'?'panel-wrong':'panel-skip'}`}>
                                                                    <div className="rqdp-header">
                                                                        <span className="rqdp-qnum">Q{selQ.questionId} — {selSubj}</span>
                                                                        <button className="rqdp-close" onClick={() => setAnalysisSelectedQ(null)}>✕</button>
                                                                    </div>
                                                                    {analysisQLoading ? (
                                                                        <div className="rqdp-spinner-wrap"><span className="rqdp-spinner"></span></div>
                                                                    ) : (<>
                                                                    {analysisPopupImages[selQ.questionId] && (
                                                                        <div className="rqdp-img-wrapper" onClick={() => openImageModal(analysisPopupImages[selQ.questionId])} style={{cursor:'zoom-in'}}>
                                                                            <img src={analysisPopupImages[selQ.questionId]} alt={`Q${selQ.questionId}`} className="rqdp-img" />
                                                                            <span className="rqdp-zoom-hint">🔍 Tap to zoom</span>
                                                                        </div>
                                                                    )}
                                                                    <div className="rqdp-answers">
                                                                        <div className="rqdp-ans-row"><span className="rqdp-ans-label">Your Answer:</span><span className={`rqdp-ans-val ${selQ.status==='correct'?'ans-correct':selQ.status==='incorrect'?'ans-wrong':'ans-skip'}`}>{selQ.userAnswer || 'Not Answered'}</span></div>
                                                                        <div className="rqdp-ans-row"><span className="rqdp-ans-label">Correct Answer:</span><span className="rqdp-ans-val ans-correct">{selQ.correctAnswer || '—'}</span></div>
                                                                        <div className="rqdp-ans-row"><span className="rqdp-ans-label">Marks:</span><span className="rqdp-ans-val">{selQ.marks > 0 ? `+${selQ.marks}` : selQ.marks}</span></div>
                                                                    </div>
                                                                    </>)}
                                                                </div>
                                                            )}
                                                        </>
                                                    );
                                                })() : <p className="loading-text">No analysis data available.</p>}
                                            </section>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {modalImage && (
                <div className="image-modal-overlay" onClick={closeImageModal}>
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={closeImageModal}>&times;</button>
                        <div className="modal-controls">
                            <button onClick={() => handleZoom(1)} disabled={zoomLevel >= 3}>Zoom In</button>
                            <button onClick={() => handleZoom(-1)} disabled={zoomLevel <= 1}>Zoom Out</button>
                            <button onClick={() => { setZoomLevel(1); setPanX(0); setPanY(0); }}>Reset View</button>
                        </div>
                        <div className="image-display-area"
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onContextMenu={handleContextMenu}
                            style={{ cursor: zoomLevel > 1 ? 'grab' : 'default' }}
                        >
                            <img
                                ref={imageRef}
                                src={modalImage}
                                alt="Full screen question"
                                style={{
                                    transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`,
                                    cursor: isDragging.current ? 'grabbing' : (zoomLevel > 1 ? 'grab' : 'default')
                                }}
                            />
                        </div>
                        <div className="modal-instructions">
                            {zoomLevel === 1 ? "Click to Zoom In. Use Zoom buttons." : "Click and drag to pan. Use Zoom buttons."}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;